/*
 * Geometry for the Network canvas: radial ego layout, cluster regions, hulls, custom d3-style
 * forces and smooth camera paths. Pure and dependency-free (d3 is ESM-only under Jest).
 */
import { NetworkAnalysis, hopDistances } from '../graphAnalytics';

export interface Point {
  x: number;
  y: number;
}

const TAU = Math.PI * 2;
const norm = (a: number) => ((a % TAU) + TAU) % TAU;

export const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
export const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

/** Circular mean of angles (radians) */
export function meanAngle(angles: number[]): number {
  let s = 0;
  let c = 0;
  angles.forEach((a) => {
    s += Math.sin(a);
    c += Math.cos(a);
  });
  return Math.atan2(s, c);
}

/**
 * Pushes angles apart until neighbours are at least `minGap` apart, keeping each close to
 * where it wants to be. Returns angles in input order.
 */
export function spreadAngles(desired: number[], minGap: number): number[] {
  const n = desired.length;
  if (n <= 1) return desired.map(norm);
  const gap = Math.min(minGap, (TAU / n) * 0.985);
  const order = desired.map((a, i) => ({ a: norm(a), i })).sort((p, q) => p.a - q.a);
  const angles = order.map((o) => o.a);
  for (let pass = 0; pass < 80; pass++) {
    let moved = false;
    for (let k = 0; k < n; k++) {
      const next = (k + 1) % n;
      let d = angles[next] - angles[k];
      if (next === 0) d += TAU;
      if (d < gap - 1e-6) {
        const push = (gap - d) / 2;
        angles[k] -= push;
        angles[next] += push;
        moved = true;
      }
    }
    if (!moved) break;
  }
  const out = new Array<number>(n);
  order.forEach((o, k) => (out[o.i] = angles[k]));
  return out;
}

export interface RadialLayout {
  positions: Map<string, Point>;
  /** ring per entity: 0 ego, 1 direct, 2 second degree, 3 further or unreachable */
  ringOf: Map<string, number>;
  rings: Array<{ hop: number; radius: number; count: number }>;
}

/**
 * Ego network on concentric rings. Direct ties are ordered by cluster so related contacts sit
 * together; second- and third-ring entities sit near the contacts that lead to them.
 */
export function radialLayout(analysis: NetworkAnalysis, egoId: string, nodeIds: string[]): RadialLayout {
  const g = analysis.graph;
  const positions = new Map<string, Point>();
  const ringOf = new Map<string, number>();
  const ego = g.index.get(egoId);
  if (ego === undefined) return { positions, ringOf, rings: [] };
  const dist = hopDistances(g, ego);
  const byRing: string[][] = [[], [], [], []];
  nodeIds.forEach((id) => {
    const i = g.index.get(id);
    const d = i === undefined ? undefined : dist.get(i);
    const ring = d === undefined ? 3 : Math.min(3, d);
    ringOf.set(id, ring);
    byRing[ring].push(id);
  });
  positions.set(egoId, { x: 0, y: 0 });
  ringOf.set(egoId, 0);

  const metric = (id: string) => analysis.metrics.get(id);
  const angle = new Map<string, number>();

  const r1 = Math.max(120, (byRing[1].length * 34) / TAU);
  const r2 = Math.max(r1 + 110, (byRing[2].length * 26) / TAU);
  const r3 = Math.max(r2 + 120, (byRing[3].length * 18) / TAU);
  const radii = [0, r1, r2, r3];

  // Ring 1: grouped by cluster, then most influential first
  const ring1 = byRing[1]
    .slice()
    .sort(
      (a, b) =>
        (metric(a)?.community ?? 0) - (metric(b)?.community ?? 0) || (metric(b)?.pagerank || 0) - (metric(a)?.pagerank || 0),
    );
  // Offset by half a step so no entity sits on the ring label at the top
  ring1.forEach((id, i) => angle.set(id, -Math.PI / 2 + (TAU * (i + 0.5)) / Math.max(1, ring1.length)));

  const placeNear = (ring: number) => {
    const ids = byRing[ring];
    if (!ids.length) return;
    const communityAngle = new Map<number, number>();
    const desired = ids.map((id, idx) => {
      const i = g.index.get(id);
      const parents = i === undefined ? [] : g.nbrs[i].map((j) => angle.get(g.ids[j])).filter((a): a is number => a !== undefined);
      if (parents.length) return meanAngle(parents);
      // No path inward: group by cluster around the circle
      const c = metric(id)?.community ?? -1;
      if (!communityAngle.has(c)) communityAngle.set(c, (communityAngle.size * 2.399963) % TAU);
      return communityAngle.get(c)! + idx * 0.001;
    });
    const spread = spreadAngles(desired, (ring === 2 ? 30 : 20) / radii[ring]);
    ids.forEach((id, k) => angle.set(id, spread[k]));
  };
  placeNear(2);
  placeNear(3);

  angle.forEach((a, id) => {
    const r = radii[ringOf.get(id) || 0];
    positions.set(id, { x: Math.cos(a) * r, y: Math.sin(a) * r });
  });

  return {
    positions,
    ringOf,
    rings: [1, 2, 3].filter((h) => byRing[h].length).map((hop) => ({ hop, radius: radii[hop], count: byRing[hop].length })),
  };
}

export interface Region {
  x: number;
  y: number;
  r: number;
}

export const regionRadius = (size: number) => 34 + 30 * Math.sqrt(Math.max(1, size));

/** Packs one circular region per group, largest in the centre, spiralling outwards */
export function clusterRegions(groups: Array<{ key: number; size: number }>, gap = 40): Map<number, Region> {
  const placed: Array<Region & { key: number }> = [];
  groups
    .slice()
    .sort((a, b) => b.size - a.size)
    .forEach(({ key, size }) => {
      const r = regionRadius(size);
      if (!placed.length) {
        placed.push({ key, x: 0, y: 0, r });
        return;
      }
      for (let s = 1; s < 4000; s++) {
        const a = s * 2.399963;
        const d = 14 * Math.sqrt(s) * 2.2;
        const x = Math.cos(a) * d;
        const y = Math.sin(a) * d;
        if (placed.every((p) => Math.hypot(p.x - x, p.y - y) >= p.r + r + gap)) {
          placed.push({ key, x, y, r });
          return;
        }
      }
      placed.push({ key, x: placed.length * 400, y: 0, r });
    });
  return new Map(placed.map(({ key, x, y, r }) => [key, { x, y, r }]));
}

/** Convex hull (monotone chain), counter-clockwise, without the repeated first point */
export function convexHull(points: Array<[number, number]>): Array<[number, number]> {
  const pts = points.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (pts.length < 3) return pts;
  const cross = (o: [number, number], a: [number, number], b: [number, number]) =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: Array<[number, number]> = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: Array<[number, number]> = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  upper.pop();
  lower.pop();
  return lower.concat(upper);
}

/** Hull around circles: each circle contributes points on its padded outline */
export function paddedHull(circles: Array<{ x: number; y: number; r: number }>, pad: number, samples = 10): Array<[number, number]> {
  const points: Array<[number, number]> = [];
  circles.forEach(({ x, y, r }) => {
    for (let k = 0; k < samples; k++) {
      const a = (TAU * k) / samples;
      points.push([x + Math.cos(a) * (r + pad), y + Math.sin(a) * (r + pad)]);
    }
  });
  return convexHull(points);
}

/* ------------------------------------------------------------------ */
/* Forces (d3-force compatible: fn(alpha) + initialize(nodes))         */
/* ------------------------------------------------------------------ */

export interface SimPoint {
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
  [key: string]: any;
}

/** Pulls each node towards its own target (null = no pull) */
export function forceTarget<N extends SimPoint>(target: (node: N) => Point | null, strength: (node: N) => number) {
  let nodes: N[] = [];
  const force: any = (alpha: number) => {
    for (const node of nodes) {
      const t = target(node);
      if (!t) continue;
      const k = strength(node) * alpha;
      node.vx = (node.vx || 0) + (t.x - (node.x || 0)) * k;
      node.vy = (node.vy || 0) + (t.y - (node.y || 0)) * k;
    }
  };
  force.initialize = (n: N[]) => {
    nodes = n;
  };
  return force;
}

/** Collision avoidance on a spatial hash grid (like d3.forceCollide, without the quadtree) */
export function forceCollideGrid<N extends SimPoint>(radius: (node: N) => number, strength = 0.7) {
  let nodes: N[] = [];
  const force: any = () => {
    const n = nodes.length;
    if (n < 2) return;
    const radii = new Float64Array(n);
    let maxR = 1;
    for (let i = 0; i < n; i++) {
      radii[i] = radius(nodes[i]);
      if (radii[i] > maxR) maxR = radii[i];
    }
    const cell = maxR * 2;
    const grid = new Map<number, number[]>();
    const px = new Float64Array(n);
    const py = new Float64Array(n);
    const keyOf = (cx: number, cy: number) => (cx + 50000) * 100000 + (cy + 50000);
    for (let i = 0; i < n; i++) {
      const node = nodes[i];
      px[i] = (node.x || 0) + (node.vx || 0);
      py[i] = (node.y || 0) + (node.vy || 0);
      const key = keyOf(Math.floor(px[i] / cell), Math.floor(py[i] / cell));
      const list = grid.get(key);
      if (list) list.push(i);
      else grid.set(key, [i]);
    }
    for (let i = 0; i < n; i++) {
      const cx = Math.floor(px[i] / cell);
      const cy = Math.floor(py[i] / cell);
      const ri = radii[i];
      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          const list = grid.get(keyOf(cx + dx, cy + dy));
          if (!list) continue;
          for (const j of list) {
            if (j <= i) continue;
            const r = ri + radii[j];
            let x = px[i] - px[j];
            let y = py[i] - py[j];
            let l = x * x + y * y;
            if (l >= r * r) continue;
            if (l === 0) {
              x = (Math.random() - 0.5) * 1e-6;
              y = (Math.random() - 0.5) * 1e-6;
              l = x * x + y * y;
            }
            l = Math.sqrt(l);
            const k = ((r - l) / l) * strength;
            const rj2 = radii[j] * radii[j];
            const wi = rj2 / (ri * ri + rj2);
            const a = nodes[i];
            const b = nodes[j];
            a.vx = (a.vx || 0) + x * k * wi;
            a.vy = (a.vy || 0) + y * k * wi;
            b.vx = (b.vx || 0) - x * k * (1 - wi);
            b.vy = (b.vy || 0) - y * k * (1 - wi);
          }
        }
      }
    }
  };
  force.initialize = (n: N[]) => {
    nodes = n;
  };
  return force;
}

/* ------------------------------------------------------------------ */
/* Camera                                                              */
/* ------------------------------------------------------------------ */

export interface View {
  /** graph coordinates at the viewport centre */
  x: number;
  y: number;
  /** zoom */
  k: number;
}

/** View that fits a set of points (with radii) into a viewport */
export function fitView(
  points: Array<{ x: number; y: number; r?: number }>,
  width: number,
  height: number,
  padding = 60,
  maxZoom = 3,
): View | null {
  if (!points.length) return null;
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  points.forEach(({ x, y, r = 0 }) => {
    x0 = Math.min(x0, x - r);
    y0 = Math.min(y0, y - r);
    x1 = Math.max(x1, x + r);
    y1 = Math.max(y1, y + r);
  });
  const w = Math.max(1, x1 - x0);
  const h = Math.max(1, y1 - y0);
  const k = Math.min(maxZoom, Math.max(0.05, Math.min((width - padding * 2) / w, (height - padding * 2) / h)));
  return { x: (x0 + x1) / 2, y: (y0 + y1) / 2, k };
}

const cosh = (x: number) => (Math.exp(x) + Math.exp(-x)) / 2;
const sinh = (x: number) => (Math.exp(x) - Math.exp(-x)) / 2;
const tanh = (x: number) => sinh(x) / cosh(x);

/**
 * Smooth zoom-and-pan path between two views (van Wijk & Nuij): zooms out while travelling far,
 * so the subject never leaves context. `at(t)` for t in [0, 1]; suggested `duration` in ms.
 */
export function flightPath(from: View, to: View, viewportWidth: number, rho = Math.SQRT2) {
  const w0 = viewportWidth / from.k;
  const w1 = viewportWidth / to.k;
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const d2 = dx * dx + dy * dy;
  const rho2 = rho * rho;
  let at: (t: number) => View;
  let S: number;
  if (d2 < 1e-12) {
    S = Math.log(w1 / w0) / rho;
    at = (t) => ({ x: from.x + t * dx, y: from.y + t * dy, k: viewportWidth / (w0 * Math.exp(rho * t * S)) });
  } else {
    const d1 = Math.sqrt(d2);
    const b0 = (w1 * w1 - w0 * w0 + rho2 * rho2 * d2) / (2 * w0 * rho2 * d1);
    const b1 = (w1 * w1 - w0 * w0 - rho2 * rho2 * d2) / (2 * w1 * rho2 * d1);
    const r0 = Math.log(Math.sqrt(b0 * b0 + 1) - b0);
    const r1 = Math.log(Math.sqrt(b1 * b1 + 1) - b1);
    S = (r1 - r0) / rho;
    at = (t) => {
      if (t >= 1) return to;
      const s = t * S;
      const u = (w0 / (rho2 * d1)) * (cosh(r0) * tanh(rho * s + r0) - sinh(r0));
      const w = (w0 * cosh(r0)) / cosh(rho * s + r0);
      return { x: from.x + u * dx, y: from.y + u * dy, k: viewportWidth / w };
    };
  }
  const duration = Math.max(450, Math.min(1100, Math.abs(S) * 520));
  return { at, duration: Number.isFinite(duration) ? duration : 700 };
}
